
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useToast } from '@/hooks/use-toast';

interface ForgotPasswordModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ForgotPasswordModal: React.FC<ForgotPasswordModalProps> = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const { resetPassword } = useAuth();
  const { t } = useLanguage();
  const { toast } = useToast();

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = await resetPassword(email);
      
      if (error) {
        toast({
          title: "Error",
          description: error.message,
          variant: "destructive"
        });
      } else {
        toast({
          title: t('auth.resetEmailSent'),
          description: t('auth.checkEmail'),
        });
        onClose();
        setEmail('');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: t('auth.genericError'),
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-md max-h-[90vh] overflow-y-auto overscroll-contain">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold">{t('auth.resetPassword')}</DialogTitle>
          <DialogDescription className="text-gray-600">
            {t('auth.resetEmailInstructions')}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleResetPassword} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="reset-email" className="text-sm font-semibold">
              {t('auth.email')}
            </Label>
            <Input
              id="reset-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('auth.email')}
              className="h-12 border-gray-300 rounded-sm focus:border-black focus:ring-1 focus:ring-black"
              required
            />
          </div>
          
          <div className="flex flex-col gap-3">
            <Button type="submit" className="w-full tesla-button" disabled={loading}>
              {loading ? t('common.loading') : t('auth.sendResetEmail')}
            </Button>
            
            <Button 
              type="button" 
              variant="ghost" 
              onClick={onClose}
              className="w-full"
            >
              {t('auth.backToSignIn')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ForgotPasswordModal;
